import { useCallback } from "react";
import { Alert } from "antd5";

import { SearchPage, SearchResultsBody } from "components/search_page/SearchPage";
import { TRANSACTION_COLUMN_FIELDS, TransactionColumn } from "components/spend_data/columns";
import { useSpendApiFilters } from "components/spend_data/hooks/useSpendApiFilters";
import { useSpendHeadline } from "components/spend_data/hooks/useSpendHeadline";
import { SpendView, useSpendUrlState } from "components/spend_data/hooks/useSpendUrlState";
import { TransactionSort } from "components/spend_data/hooks/useTransactionsTable";
import SpendDataFilters from "components/spend_data/SpendDataFilters";
import { SpendResultsSummary } from "components/spend_data/SpendResultsSummary";
import { SpendStartingState } from "components/spend_data/SpendStartingState";
import { SpendTransactionsView } from "components/spend_data/SpendTransactionsView";
import { SpendTrendsView } from "components/spend_data/trends/SpendTrendsView";
import BetaIcon from "components/ui/beta_icon/BetaIcon";
import SegmentedControl, {
  SegmentedControlOption,
} from "components/ui/segmented_control/SegmentedControl";
import { isQueryableSpendFilter } from "lib/hooks/api/spend/spendFilters";
import { useConfirmUserDataSubscription } from "lib/providers/Paywall";

const VIEW_MODE_OPTIONS: SegmentedControlOption<SpendView["mode"]>[] = [
  { value: "table", label: "Transactions" },
  { value: "trends", label: "Trends" },
];

/**
 * The spend search page. It holds the filters and the headline; the view on screen owns its
 * own query, export and empty state.
 */
export function SpendDataPage() {
  const {
    filters,
    appliedFiltersCount,
    debouncedSetFilters,
    clearFilters,
    view,
    setView,
    applyTemplate,
    columns,
    setColumns,
  } = useSpendUrlState();
  const apiFilters = useSpendApiFilters(filters);

  // Runs once upon load, checks whether user is subscribed to spend data or not, if not, then
  // open the paywall modal and upon close will go back to my feed
  useConfirmUserDataSubscription("SUPPLIERS", "SPEND");

  const hasFilters = appliedFiltersCount > 0;
  const isDateRangeRequired = hasFilters && !isQueryableSpendFilter(apiFilters);
  const showsResults = hasFilters && !isDateRangeRequired;

  const headline = useSpendHeadline(apiFilters, { enabled: showsResults });

  const columnFields = (columns ?? TRANSACTION_COLUMN_FIELDS) as TransactionColumn[];
  const setColumnFields = useCallback(
    (fields: TransactionColumn[]) => setColumns(fields, TRANSACTION_COLUMN_FIELDS),
    [setColumns],
  );
  const setTransactionSort = useCallback(
    (transactionSort: TransactionSort) => setView({ transactionSort }),
    [setView],
  );

  const renderBody = () => {
    if (!hasFilters) {
      return (
        <SpendStartingState onSelect={(preset) => applyTemplate(preset.filters, preset.view)} />
      );
    }
    if (isDateRangeRequired) {
      return (
        <SearchResultsBody>
          <Alert
            message="Add a date range to load spend results"
            description="Spend summaries require a date filter."
            type="warning"
            showIcon
            style={{ maxWidth: 860 }}
          />
        </SearchResultsBody>
      );
    }
    if (view.mode === "trends") {
      return (
        <SearchResultsBody>
          <SpendTrendsView filters={apiFilters} view={view} onViewChange={setView} />
        </SearchResultsBody>
      );
    }
    return (
      <SearchResultsBody>
        <SpendTransactionsView
          filters={apiFilters}
          columnFields={columnFields}
          onColumnFieldsChange={setColumnFields}
          sort={view.transactionSort}
          onSortChange={setTransactionSort}
        />
      </SearchResultsBody>
    );
  };

  return (
    <SearchPage
      storageKey="spend"
      title="Spend"
      titleBadge={<BetaIcon betaFeature="spendData" />}
      filters={<SpendDataFilters filters={filters} setFilters={debouncedSetFilters} />}
      appliedFiltersCount={appliedFiltersCount}
      onClearFilters={clearFilters}
      summary={
        showsResults ? (
          <SpendResultsSummary
            transactionCount={headline.transactionCount}
            totalValue={headline.totalValue}
          />
        ) : undefined
      }
      isLoadingSummary={headline.isLoading}
      viewMode={
        hasFilters && (
          <SegmentedControl
            value={view.mode}
            onChange={(next) => setView({ mode: next })}
            options={VIEW_MODE_OPTIONS}
            aria-label="View mode"
          />
        )
      }
    >
      {renderBody()}
    </SearchPage>
  );
}

export default SpendDataPage;
